import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { deleteConversation } from '../store/conversationSlice';
import { useNavigate } from 'react-router-dom';

const DeleteConversationModal = ({ 
  open, 
  onClose, 
  conversation 
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { activeConversation } = useSelector((state) => state.conversation);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleClose = () => {
    setIsSubmitting(false);
    onClose();
  };

  const handleDelete = async () => {
    if (conversation && !isSubmitting) {
      // console.log('Confirming delete for conversation:', conversation);
      setIsSubmitting(true);
      try {
        await dispatch(deleteConversation(conversation.conversation_id)).unwrap();
        handleClose();

        // If we're deleting the active conversation, navigate to home
        if (activeConversation?.conversation_id === conversation.conversation_id) {
          navigate('/');
        }
      } catch (error) {
        console.error('Error deleting conversation:', error);
      } finally {
        setIsSubmitting(false);
      }
    } else {
      console.log('No conversation to delete or already submitting:', { conversation, isSubmitting });
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm" 
      fullWidth
      aria-labelledby="delete-dialog-title"
      aria-describedby="delete-dialog-description"
      BackdropProps={{
        sx: {
          backgroundColor: 'rgba(0, 0, 0, 0.3)', // lighter dark
        }
      }}
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: '20px',
          background: 'rgb(255, 255, 255, 0.9)',
          backdropFilter: 'blur(6px)',
          WebkitBackdropFilter: 'blur(6px)',
          border: '2px solid rgba(200, 200, 200, 0.3)',
          boxShadow: '0 8px 30px rgba(0, 0, 0, 0.08)',
        },
      }}
    >
      <DialogTitle id="delete-dialog-title" sx={{ marginTop: '10px', padding: '10px 24px' }}>Delete Chat?</DialogTitle>
      <DialogContent sx={{ paddingBottom: '5px' }}>
        <Typography id="delete-dialog-description">
          The chat will be deleted and removed from your chat history. This action cannot be undone.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ marginBottom: '10px', marginRight: '17px' }}>
        <Button 
          onClick={handleClose}
          disabled={isSubmitting}
          sx={{
            borderRadius: '12px',
            background: 'rgba(255, 255, 255, 0.4)',
            border: '1px solid rgba(150, 150, 150, 0.3)',
            color: '#71717A',
            cursor: 'pointer',
            transition: '0.3s ease',
            backdropFilter: 'blur(6px)',
            textTransform: 'none',
            width: '100px',
            '&:hover': {
                background: 'rgba(255, 255, 255, 0.6)',
                borderColor: 'rgba(150, 150, 150, 0.4)',
                boxShadow: '0 0 10px rgba(18, 157, 232, 0.15)',
            },
          }}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleDelete} 
          variant="contained" 
          color="error"
          disabled={isSubmitting}
          sx={{
            borderRadius: '12px',
            background: 'rgba(211, 47, 47, 0.1)', // red background
            boxShadow: 'none',
            border: '1px solid rgba(211, 47, 47, 0.2)', // red border
            color: '#71717A',
            cursor: 'pointer',
            transition: '0.3s ease',
            backdropFilter: 'blur(6px)',
            textTransform: 'none',
            width: '100px',
            '&:hover': {
                background: 'rgba(211, 47, 47, 0.2)', // darker red on hover
                borderColor: 'rgba(211, 47, 47, 0.5)',
                boxShadow: '0 0 10px rgba(211, 47, 47, 0.15)', // red glow
            },
          }}
        >
          {isSubmitting ? 'Deleting...' : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteConversationModal; 